import { z } from 'zod';

// ID Schema
export const idSchema = z.number().positive();

// Name schema with overloaded options
export interface NameSchemaOptions {
  name?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  allowNumbers?: boolean;
  allowSpecialChars?: boolean;
  customPattern?: RegExp;
  customMessage?: string;
}

export const nameSchemaHelper = 'Please enter Name';
export const createNameSchema = (options: NameSchemaOptions = {}) => {
  const {
    name = 'Name',
    minLength = 1,
    maxLength,
    required = true,
    allowNumbers = false,
    allowSpecialChars = false,
    customPattern,
    customMessage,
  } = options;

  let schema = z.string().trim();

  if (minLength > 0) {
    schema = schema.min(minLength, {
      message:
        customMessage ||
        `${name} must be at least ${minLength} character${minLength > 1 ? 's' : ''}`,
    });
  }

  if (maxLength) {
    schema = schema.max(maxLength, {
      message:
        customMessage || `${name} must not exceed ${maxLength} characters`,
    });
  }

  // Apply pattern validation
  if (customPattern) {
    schema = schema.regex(customPattern, {
      message: customMessage || `${name} contains invalid characters`,
    });
  } else {
    // Default pattern based on options
    let pattern: RegExp;
    if (allowNumbers && allowSpecialChars) {
      pattern = /^[A-Za-z0-9\s\-_.,'()]+$/;
    } else if (allowNumbers) {
      pattern = /^[A-Za-z0-9\s]+$/;
    } else if (allowSpecialChars) {
      pattern = /^[A-Za-z\s\-_.,'()]+$/;
    } else {
      pattern = /^[A-Za-z\s]+$/;
    }
    schema = schema.regex(pattern, {
      message: customMessage || `${name} contains invalid characters`,
    });
  }

  // Handle required/optional
  if (!required) {
    return schema.optional();
  }

  return schema;
};

// Email Schema
export const emailHelper = 'Valid email address is required';
export const emailHelperOptional = `${emailHelper}, optional`;
export const emailSchema = z
  .string()
  .trim()
  .email({ message: 'Invalid email address' })
  .min(1, { message: 'Email is required' })
  .max(300, { message: 'Email must be less than 300 characters' });

// Phone Schema - valid phone number (no alpha characters or special characters allowed other than +, -, (, ), space)
export const phoneHelper = 'Please enter a valid phone number';
export const phoneHelperOptional = `${phoneHelper}, optional`;
export const phoneSchema = z
  .string()
  .trim()
  .min(1, { message: 'Phone number is required' })
  .max(20, { message: 'Phone number must not exceed 20 characters' })
  .regex(/^[\d\s\+\-\(\)]+$/, {
    message:
      'Phone number can only contain digits, spaces, plus signs, hyphens, and parentheses',
  });

// Country Schema - alpha characters only, no special characters, no numbers, min 2 characters, max 100 characters
export const countryHelper = 'Please choose a country from the list';
export const countryHelperOptional = `${countryHelper}, optional`;
export const countrySchema = z
  .string()
  .trim()
  .min(2, { message: 'Please choose a country from the list' })
  .max(100, { message: 'Please choose a country from the list' })
  .regex(/^[A-Za-z\s]+$/, {
    message: 'Country can only contain letters and spaces',
  });

// Address Schema - alphanumeric characters only, no special characters other than space, ,, -, ., ', (, ), #, min 2 characters, max 1000 characters
export const addressHelper =
  'Address must be 2-1000 characters, alphanumeric with allowed special characters: space, comma, hyphen, period, apostrophe, parentheses, hash';
export const addressHelperOptional = `${addressHelper}, optional`;
export const addressSchema = z
  .string()
  .trim()
  .min(2, { message: 'Address must be at least 2 characters' })
  .max(1000, { message: 'Address must not exceed 1000 characters' })
  .regex(/^[A-Za-z0-9\s,\-.'()#]+$/, {
    message:
      'Address can only contain alphanumeric characters, spaces, commas, hyphens, periods, apostrophes, parentheses, and hash symbols',
  });

// Zip Code Schema - min 5 characters, max 10 characters, alphanumeric only, no special characters except space
export const zipcodeHelper =
  'Zip code must be 5-10 characters, alphanumeric with spaces only';
export const zipcodeHelperOptional = `${zipcodeHelper}, optional`;
export const zipcodeSchema = z
  .string()
  .trim()
  .min(5, { message: 'Zip code must be at least 5 characters' })
  .max(10, { message: 'Zip code must not exceed 10 characters' })
  .regex(/^[A-Za-z0-9\s]+$/, {
    message: 'Zip code can only contain alphanumeric characters and spaces',
  });

// Description Schema - alphanumeric characters only, no special characters other than space, ,, -, ., ', (, ), #, min 1 character, max 10000 characters
export const descriptionHelper =
  'Description must be 1-10000 characters, alphanumeric with allowed special characters: space, comma, hyphen, period, apostrophe, parentheses, hash';
export const descriptionHelperOptional = `${descriptionHelper}, optional`;

// Text Field schema with overloaded options
export interface TextFieldSchemaOptions {
  name?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  allowNumbers?: boolean;
  allowSpecialChars?: boolean;
  customPattern?: RegExp;
  customMessage?: string;
}

export const createTextFieldSchema = (options: TextFieldSchemaOptions = {}) => {
  const {
    name = 'Field',
    minLength = 1,
    maxLength = 10000,
    required = true,
    allowNumbers = true,
    allowSpecialChars = true,
    customPattern,
    customMessage,
  } = options;

  let schema = z.string().trim();

  // Apply pattern validation
  if (customPattern) {
    schema = schema.regex(customPattern, {
      message: customMessage || `${name} contains invalid characters`,
    });
  } else {
    // Default pattern based on options
    let pattern: RegExp;
    if (allowNumbers && allowSpecialChars) {
      pattern = /^[A-Za-z0-9\s,\-.'"()?#]+$/;
    } else if (allowNumbers) {
      pattern = /^[A-Za-z0-9\s]+$/;
    } else if (allowSpecialChars) {
      pattern = /^[A-Za-z\s,\-.'"()?#]+$/;
    } else {
      pattern = /^[A-Za-z\s]+$/;
    }
    schema = schema.regex(pattern, {
      message: customMessage || `${name} contains invalid characters`,
    });
  }

  // Apply length validations after pattern validation
  if (minLength > 0) {
    schema = schema.min(minLength, {
      message:
        customMessage ||
        `${name} must be at least ${minLength} character${minLength > 1 ? 's' : ''}`,
    });
  }

  if (maxLength) {
    schema = schema.max(maxLength, {
      message:
        customMessage || `${name} must not exceed ${maxLength} characters`,
    });
  }

  // Handle required/optional
  if (!required) {
    // For optional fields, allow empty strings or the validated schema
    return z.union([z.literal(''), schema]).optional();
  }

  return schema;
};

// Helper function to generate Text Field helper text
export interface TextFieldHelperTextOptions {
  name?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  allowNumbers?: boolean;
  allowSpecialChars?: boolean;
  customHelperText?: string;
}
export const createTextFieldHelperText = (
  options: TextFieldHelperTextOptions = {},
) => {
  const {
    name = 'Field',
    minLength = 1,
    maxLength = 10000,
    required = true,
    allowNumbers = true,
    allowSpecialChars = true,
    customHelperText,
  } = options;

  if (customHelperText) {
    return customHelperText;
  }

  let helperText = `${name} ${required ? 'must be' : 'can be'} ${minLength}-${maxLength} characters`;

  if (allowNumbers && allowSpecialChars) {
    helperText +=
      ', alphanumeric with allowed special characters: space, comma, hyphen, period, apostrophe, parentheses, hash, question mark';
  } else if (allowNumbers) {
    helperText += ', alphanumeric characters only';
  } else if (allowSpecialChars) {
    helperText +=
      ', letters with allowed special characters: space, comma, hyphen, period, apostrophe, parentheses, hash, question mark';
  } else {
    helperText += ', letters and spaces only';
  }

  if (!required) {
    helperText += ' (optional)';
  }

  return helperText;
};
